/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { STARCTypography } from '@/components/STARCTypography';
import type { FormattedContentStackCategoryPages } from '@/features/contentstack/interface';
import { CMSBannerSplit } from '@/features/contentstack/components/CMSBannerSplit/CMSBannerSplit';
import { CMSGridBanner } from '@/features/contentstack/components/CMSGridBanner/CMSGridBanner';
import { CMSImageCategory } from '@/features/contentstack/components/CMSImageCategory/CMSImageCategory';
import { CMSImageContent } from '@/features/contentstack/components/CMSImageContent/CMSImageContent';
import { CMSImageContentBackground } from '@/features/contentstack/components/CMSImageContentBackground/CMSImageContentBackground';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { CMSFaq } from '@/features/contentstack/components/CMSFaq/CMSFaq';
import { CMSFeaturedContent } from '@/features/contentstack/components/CMSFeaturedContent/CMSFeaturedContent';
import { CMSSpacer } from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSProductChart } from '@/features/contentstack/components/CMSProductChart/CMSProductChart';
import { CMSRichText } from '@/features/contentstack/components/CMSRichText/CMSRichText';
import { CMSVideoContent } from '@/features/contentstack/components/CMSVideoContent/CMSVideoContent';
import { CMSGrid } from '@/features/contentstack/components/CMSGrid/CMSGrid';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import { CMSTarget } from '@/features/contentstack/components/CMSTarget/CMSTarget';
import { CMSCategoryGrid } from '@/features/contentstack/components/CMSCategoryGrid/CMSCategoryGrid';
import { CMSCitrus } from '@/features/contentstack/components/CMSCitrus/CMSCitrus';
import { CMSImageVideoSplit } from '@/features/contentstack/components/CMSImageVideoSplit/CMSImageVideoSplit';
import { CMSBreadcrumbs } from './CMSBreadcrumbs/CMSBreadcrumbs';
import { CMSMostPopularParts } from './CMSMostPopularParts/CMSMostPopularParts';
import { CMSShopByCategory } from './CMSShopByCategory/CMSShopByCategory';
import { CMSSmmy } from './CMSSmmy/CMSSmmy';
import { CMSSeoCopy } from './CMSSeoCopy/CMSSeoCopy';
import { CMSPopularSearches } from './CMSPopularSearches/CMSPopularSearches';
import { CMSCategoryYMME } from './CMSCategoryYMME/CMSCategoryYMME';
import { type CertonaBlockProps } from '@/features/certona';
import Title from '@/components/AZCustomComponent/Title';
import { useCitrusCategorypageData } from '@/features/citrus/api/getCitrusCategorypageData';
import { usePageType } from '../../api/getPageType';
import styles from './CMSModularHandler.module.scss';
import { type CategoryFAQData } from '../../interface';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { FaqComp } from '../FAQ';
import { useMemo } from 'react';
import { SearchStore } from '@/components/Store/SearchStore/SearchStore';
import { useScrollIntoViewDOM } from '@/hooks/useScrollIntoView';
import { CMSDealsSplit } from '@/features/contentstack/components/CMSDealsSplit/CMSDealsSplit';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';

type Props = {
  content: FormattedContentStackCategoryPages[Exclude<
    keyof FormattedContentStackCategoryPages,
    'prefooter'
  >];
  catLabel?: string;
  certonaRecommendations?: CertonaBlockProps['certonaRecommendations'];
  faqs?: CategoryFAQData | undefined;
};

function CMSCitrusSpacer({ children }: { children: React.ReactNode }) {
  return (
    <CMSSpacer removeHorizontalSpacing shouldUseTopCategorySpacing>
      {children}
    </CMSSpacer>
  );
}

export const CMSModularHandler = ({ catLabel, content, certonaRecommendations, faqs }: Props) => {
  const { data: pageTypeData } = usePageType();
  const isMMCategoryPage = !!pageTypeData?.makeModelYearPath;
  const locale = useLocale();
  const { data: citrusData } = useCitrusCategorypageData(pageTypeData?.catalogRepositoryName ?? '');

  let isFirstBannerRendered = false;
  const hasBanner = useMemo(
    () =>
      content?.some(
        (modularBlock) => 'banner' in modularBlock && modularBlock.banner?.banner?.[0]?.asset_size,
      ) ?? false,
    [content],
  );

  useScrollIntoViewDOM();
  return (
    <STARCTypography>
      {content?.map((modularBlock, i) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if (
          'store_locator' in modularBlock &&
          modularBlock?.store_locator?.store_locator?.[0]?.enabled
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('store_locator')}>
              <SearchStore
                make={pageTypeData?.make}
                content={modularBlock?.store_locator.store_locator?.[0]}
              />
            </CMSSpacer>
          );
        }
        if ('banner_split' in modularBlock && modularBlock.banner_split?.banner_split?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('banner_split')}>
              <CMSBannerSplit content={modularBlock.banner_split.banner_split[0]} />
            </CMSSpacer>
          );
        }

        if ('breadcrumb' in modularBlock && modularBlock.breadcrumb?.enable) {
          return (
            <CMSSpacer
              key={i}
              shouldUseTitleSpacing
              removeHorizontalSpacing
              id={getBlockId('breadcrumb')}
            >
              <CMSBreadcrumbs />
              {catLabel && (
                <Title overRuleH1="h1" variant="lg" firstText={catLabel || ''} swapStyle />
              )}
            </CMSSpacer>
          );
        }

        if ('most_popular_parts' in modularBlock && modularBlock.most_popular_parts?.enable) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('most_popular_parts')}>
              <CMSMostPopularParts />
            </CMSSpacer>
          );
        }

        if ('banner' in modularBlock && modularBlock.banner?.banner?.[0]?.asset_size) {
          if (!isFirstBannerRendered) {
            isFirstBannerRendered = true;
            return (
              <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('banner')}>
                <CMSBanner content={modularBlock.banner.banner[0]} priority />
              </CMSSpacer>
            );
          }
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('banner')}>
              <CMSBanner content={modularBlock.banner.banner[0]} />
            </CMSSpacer>
          );
        }

        if ('grid_banner' in modularBlock && modularBlock.grid_banner?.grid_banner?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('grid_banner')}>
              <CMSGridBanner content={modularBlock.grid_banner.grid_banner[0]} />
            </CMSSpacer>
          );
        }

        if ('image_content' in modularBlock && modularBlock.image_content?.image_content?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('image_content')}>
              <CMSImageContent content={modularBlock.image_content.image_content[0]} />
            </CMSSpacer>
          );
        }

        if ('blogtype_grid' in modularBlock && modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('blogtype_grid')}>
              <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
            </CMSSpacer>
          );
        }

        if (modularBlock.deals_split?.deals_split?.[0]) {
          return (
            <CMSSpacer key={i} id={getBlockId('deals_split')}>
              <CMSDealsSplit content={modularBlock.deals_split?.deals_split[0]} />
            </CMSSpacer>
          );
        }

        if (
          'image_video_split' in modularBlock &&
          modularBlock.image_video_split?.image_video_split?.[0]
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('image_video_split')}>
              <CMSImageVideoSplit content={modularBlock.image_video_split.image_video_split[0]} />
            </CMSSpacer>
          );
        }

        if (
          'image_content_background' in modularBlock &&
          modularBlock.image_content_background?.image_content_background?.[0]
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('image_content_background')}>
              <CMSImageContentBackground
                content={modularBlock.image_content_background.image_content_background[0]}
              />
            </CMSSpacer>
          );
        }

        if (
          'featured_content' in modularBlock &&
          modularBlock.featured_content?.featured_content?.[0]
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('featured_content')}>
              <CMSFeaturedContent content={modularBlock.featured_content.featured_content[0]} />
            </CMSSpacer>
          );
        }

        if (
          'image_category_grid' in modularBlock &&
          modularBlock.image_category_grid?.image_category_grid?.[0]
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('image_category_grid')}>
              <CMSImageCategory content={modularBlock.image_category_grid.image_category_grid[0]} />
            </CMSSpacer>
          );
        }

        if ('category_grid' in modularBlock && modularBlock.category_grid?.category_grid?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('category_grid')}>
              <CMSCategoryGrid content={modularBlock.category_grid.category_grid[0]} />
            </CMSSpacer>
          );
        }

        if ('shop_by_category' in modularBlock && modularBlock.shop_by_category?.enable) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('shop_by_category')}>
              <CMSShopByCategory />
            </CMSSpacer>
          );
        }

        if ('faq' in modularBlock && modularBlock.faq?.faq?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('faq')}>
              <CMSFaq content={modularBlock.faq.faq[0]} />
            </CMSSpacer>
          );
        }
        if ('product_grid' in modularBlock && modularBlock.product_grid?.product_grid?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('product_grid')}>
              <CMSProductTypeGrid
                content={modularBlock.product_grid.product_grid[0]}
                gridIndex={i}
              />
            </CMSSpacer>
          );
        }
        if ('product_chart' in modularBlock && modularBlock.product_chart?.product_chart?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('product_chart')}>
              <CMSProductChart content={modularBlock.product_chart.product_chart[0]} />
            </CMSSpacer>
          );
        }

        if ('target_div' in modularBlock && modularBlock.target_div?.target_div?.[0]) {
          return (
            <CMSSpacer
              key={i}
              removeHorizontalSpacing
              removeVerticalSpacing
              id={getBlockId('target_div')}
            >
              <CMSTarget content={modularBlock.target_div.target_div[0]} />
            </CMSSpacer>
          );
        }

        if (
          'rich_text_editor' in modularBlock &&
          modularBlock.rich_text_editor?.rich_text_editor?.[0]
        ) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('rich_text_editor')}>
              <CMSRichText content={modularBlock.rich_text_editor.rich_text_editor[0]} />
            </CMSSpacer>
          );
        }

        if ('video_content' in modularBlock && modularBlock.video_content?.video_content?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('video_content')}>
              <CMSVideoContent
                content={modularBlock.video_content.video_content[0]}
                preload="none"
              />
            </CMSSpacer>
          );
        }

        if ('parttype_grid' in modularBlock && modularBlock.parttype_grid?.parttype_grid?.[0]) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('parttype_grid')}>
              <CMSGrid content={modularBlock.parttype_grid.parttype_grid[0]} />
            </CMSSpacer>
          );
        }

        if (
          'shop_by_make_category_by_model' in modularBlock &&
          modularBlock.shop_by_make_category_by_model?.enable
        ) {
          return (
            <CMSSpacer
              key={i}
              removeHorizontalSpacing
              id={getBlockId('shop_by_make_category_by_model')}
            >
              <CMSSmmy />
            </CMSSpacer>
          );
        }

        if ('seo_copy' in modularBlock && modularBlock.seo_copy?.enable) {
          return (
            <div id={getBlockId('seo_copy')} key={i}>
              <CMSSpacer removeHorizontalSpacing>
                <CMSSeoCopy key={i} descriptionClassName={styles.seoCopyDescriptionContainer} />
              </CMSSpacer>
              {locale === countryCodes.us && faqs?.length ? (
                <CMSSpacer removeHorizontalSpacing>
                  <FaqComp faqs={faqs} />
                </CMSSpacer>
              ) : null}
            </div>
          );
        }

        if ('popular_searches' in modularBlock && modularBlock.popular_searches?.enable) {
          return (
            <div id={getBlockId('popular_searches')} key={i}>
              <CMSPopularSearches />
            </div>
          );
        }

        if ('certona_category_block' in modularBlock && modularBlock.certona_category_block) {
          const recContent = {
            title: 'Certona Content Block',
            theme: modularBlock.certona_category_block.theme_code ?? 'white',
            container_id: modularBlock.certona_category_block.container_id ?? '',
          };
          const recommendationItem = certonaRecommendations?.find(
            (item) => item.scheme === recContent.container_id,
          );
          return recommendationItem?.display !== 'no' && recommendationItem?.items.length ? (
            <CMSSpacer key={i} removeHorizontalSpacing id={getBlockId('certona_category_block')}>
              <CMSCertona content={recContent} certonaRecommendations={certonaRecommendations} />
            </CMSSpacer>
          ) : null;
        }

        if ('ymme' in modularBlock && modularBlock.ymme?.enable) {
          return (
            <CMSSpacer key={i} removeHorizontalSpacing shouldReduceSpacing id={getBlockId('ymme')}>
              <CMSCategoryYMME />
            </CMSSpacer>
          );
        }

        if (
          'citrus_ad' in modularBlock &&
          modularBlock.citrus_ad?.citrus_ad?.[0] &&
          !isMMCategoryPage
        ) {
          return (
            <div id={getBlockId('citrus_ad')} key={i}>
              <CMSCitrus
                content={modularBlock.citrus_ad.citrus_ad[0]}
                citrusAds={citrusData?.ads}
                priority={!hasBanner}
                SpacerComponent={CMSCitrusSpacer}
              />
            </div>
          );
        }

        if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
          return (
            <CMSSpacer
              key={i}
              removeHorizontalSpacing
              {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                id: modularBlock.pencil_banner.pencil_banner[0].component_id,
              })}
            >
              <CMSPencilBanner content={modularBlock.pencil_banner.pencil_banner[0]} />
            </CMSSpacer>
          );
        }

        return null;
      })}
    </STARCTypography>
  );
};
